import { connectLitClient, litClient } from '@polaris/core/mpc'
import { useCallback, useRef } from 'react'

/**
 * Hook for managing the Lit client.
 *
 * @returns An object containing:
 *   - `litClient`: The Lit client
 *   - `connect`: Function to connect to the Lit client
 */
export function useLitClient() {
  const connectingPromiseRef = useRef<Promise<void> | null>(null)

  /**
   * Connects to the Lit client.
   * Only initiates a new connection if not already connected or there is no connection attempt in progress.
   *
   * @returns A promise that resolves when the connection is complete.
   * @throws Error if the connection fails.
   */
  const connect = useCallback(async () => {
    if (litClient?.ready) {
      return
    }

    // If a connection is already in progress, return the promise
    if (connectingPromiseRef.current) {
      return connectingPromiseRef.current
    }

    // Set the connecting promise to prevent multiple connections
    const connectPromise = connectLitClient()
    connectingPromiseRef.current = connectPromise

    // Clear the connecting promise when the connection is complete
    connectPromise.finally(() => {
      connectingPromiseRef.current = null
    })

    await connectPromise
  }, [])

  return {
    litClient,
    connect,
  }
}
